import Header from "@/components/common/head";
import NavBar from "@/components/common/navbar";
import type { PoolProps } from "@/components/landing/topPools";
import { pools } from "@/utils/dummy";
import { formatToK } from "@/utils/numFormatter";
import useDebounce from "@/utils/useDebounce";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FixedSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

function Row({ index, style, data }: { index: number; style: any; data: any }) {
  const row = data[index];

  const router = useRouter();

  return (
    <div
      style={style}
      onClick={() => router.push(`/pools/${row?.id}`)}
      className="tr flex w-full cursor-pointer items-center justify-between rounded-md border-b-[3px] border-black bg-[#151515] py-6 hover:bg-[#151515]/50"
    >
      <div className="td flex md:w-[60%] flex-col items-start gap-3 whitespace-nowrap py-4 pl-4 text-sm font-medium md:pl-10">
        <div className="flex items-center gap-3">
          <Image
            src={row?.firstToken?.image}
            height={30}
            width={30}
            quality={100}
            alt={row?.firstToken?.type}
            className="object-contain"
          />
          <Image
            src={row?.secondToken?.image}
            height={30}
            width={30}
            quality={100}
            alt={row?.secondToken?.type}
            className="-ml-4 object-contain"
          />
          <h3 className="text-base font-semibold text-white">
            {`${row?.firstToken?.type}/${row?.secondToken?.type}`}
          </h3>
        </div>
        <div className="flex flex-col gap-1 lg:hidden">
          <span className="whitespace-nowrap rounded-r-[6px] bg-[#151515] text-sm font-[300]">
            24h Volume: ${formatToK(row?.volume?.twentyFourHour)}
          </span>
          <span className="whitespace-nowrap rounded-r-[6px] bg-[#151515] text-sm font-[300]">
            7d Volume: ${formatToK(row?.volume?.sevenDay)}
          </span>
        </div>
      </div>
      <div className="td md:w-[13%] pr-4 lg:pr-0 whitespace-nowrap py-4 text-base font-[300] md:text-right lg:text-sm">
        ${formatToK(row?.tvl)}
      </div>
      <div className="td hidden w-[13%] whitespace-nowrap py-4 pl-2 text-sm font-[300] md:text-right lg:block">
        ${formatToK(row?.volume?.twentyFourHour)}
      </div>
      <div className="td hidden w-[13%] whitespace-nowrap py-4 pl-2 text-sm font-[300] md:justify-end  md:pr-10 md:text-right lg:block">
        ${formatToK(row?.volume?.sevenDay)}
      </div>
    </div>
  );
}

export default function PoolsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const searchQuery = useSelector(
    (state: { common: { searchQuery: string } }) => state.common.searchQuery
  );

  const debouncedValue = useDebounce(searchQuery, 500);

  const [allPools, setAllPools] = useState(pools);

  useEffect(() => {
    if (allPools && debouncedValue?.length > 0) {
      const poolsFound: PoolProps[] = [];
      allPools.forEach((pl) => {
        if (
          pl.firstToken.type
            .toLowerCase()
            .includes(debouncedValue.toLowerCase()) ||
          pl.secondToken.type
            .toLowerCase()
            .includes(debouncedValue.toLowerCase())
        ) {
          poolsFound.push(pl);
        }
      });
      if (poolsFound.length > 0) {
        setAllPools(poolsFound);
      } else {
        setAllPools(pools);
      }
    }

    if (debouncedValue.length === 0) {
      setAllPools(pools);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const [sortOrder, setSortOrder] = useState("desc");

  const [items, setItems] = useState<PoolProps[]>(
    pools.slice(0, 15).sort((a, b) => b.tvl - a.tvl)
  );

  const fetchMoreData = () => {
    setLoading(true);
    setTimeout(() => {
      setItems((prevItems) =>
        prevItems.concat(
          pools
            .slice(items.length, items.length + 15)
            .sort((a, b) => b.tvl - a.tvl)
        )
      );
      setLoading(false);
    }, 500);
  };

  const data = React.useMemo(() => items, [items]);
  const limit = 1000;
  const hasNextPage = React.useMemo(() => data.length <= limit, [data, limit]);

  const [isMobile, setIsMobile] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [listHeight, setListHeight] = useState(0);

  useEffect(() => {
    setIsReady(true);
    setIsMobile(window.innerWidth < 1024);
    setListHeight(window.innerHeight - 250);
  }, []);

  useEffect(() => {
    // listen for resize, then set state to window width
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      setListHeight(window.innerHeight - 250);
    };

    window.addEventListener("resize", handleResize);

    // clean up
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!isReady || listHeight === 0) return null;

  return (
    <>
      <Header />
      <main className={`w-full bg-black px-6 pb-32 md:px-8`}>
        <NavBar />
        <section className="mx-auto mt-48 flex w-full max-w-7xl flex-col">
          <div className="flex w-full justify-between">
            <h2 className="text-xl font-medium text-white">All Pools</h2>
          </div>

          <div className="relative mt-4 w-full">
            <div
              className="min-w-full table-fixed rounded-lg border-[1px] border-[#1B1B1B]"
              style={{ height: "100%", width: "100%" }}
            >
              <div className="w-full text-sm font-[300] text-[#9D9D9D]">
                <div className="tr mb-1 flex w-full justify-between border-b-[1px] border-[#1B1B1B] px-2">
                  <div className="py-3.5 text-left md:w-[60%] md:pl-10">
                    Pool
                  </div>
                  <div
                    onClick={() => {
                      if (sortOrder === "desc") {
                        setSortOrder("asc");
                        setItems(items.sort((a, b) => a.tvl - b.tvl));
                      }
                      if (sortOrder === "asc") {
                        setSortOrder("desc");
                        setItems(items.sort((a, b) => b.tvl - a.tvl));
                      }
                    }}
                    className="flex cursor-pointer justify-end py-3.5 text-left md:w-[13%] md:text-right"
                  >
                    TVL
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className={`h-5 w-5 transition-all duration-300 ease-in ${
                        sortOrder === "desc" && "rotate-180"
                      }`}
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 5a.75.75 0 01.75.75v6.638l1.96-2.158a.75.75 0 111.08 1.04l-3.25 3.5a.75.75 0 01-1.08 0l-3.25-3.5a.75.75 0 111.08-1.04l1.96 2.158V5.75A.75.75 0 0110 5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="hidden w-[13%] py-3.5 text-left md:text-right lg:block">
                    Volume (24H)
                  </div>
                  <div className="hidden w-[13%] py-3.5 text-left md:justify-end md:pr-10 md:text-right lg:block">
                    Volume (7D)
                  </div>
                </div>
              </div>
              <div className="w-full px-2 text-white">
                {isLoading ? (
                  [...Array(10)].map((_, index) => (
                    <div
                      key={index}
                      className="my-2 flex h-[120px] w-full items-center justify-center whitespace-nowrap rounded-l-[6px] rounded-r-[6px] bg-[#151515] px-4 text-sm text-white lg:h-[55px]"
                    >
                      <div className="my-auto h-[60%] w-full animate-pulse rounded-[15px] bg-[#2C2C2C]" />
                    </div>
                  ))
                ) : (
                  <InfiniteLoader
                    isItemLoaded={(index) => index < data.length}
                    itemCount={hasNextPage ? data.length + 1 : data.length}
                    loadMoreItems={fetchMoreData}
                  >
                    {({ onItemsRendered, ref }) => (
                      <FixedSizeList
                        height={listHeight}
                        itemCount={data.length}
                        itemSize={isMobile ? 120 : 60}
                        width={"100%"}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        className="no-scrollbar"
                      >
                        {({ index, style }) => (
                          <Row index={index} style={style} data={data} />
                        )}
                      </FixedSizeList>
                    )}
                  </InfiniteLoader>
                )}
                {pools.length === 0 && (
                  <div className="mb-1 w-full whitespace-nowrap rounded-md bg-[#2C2C2C] px-4 py-4 text-sm text-white">
                    <h2>No data available</h2>
                  </div>
                )}
              </div>
            </div>
            {loading && (
              <div className="relative my-8 flex w-full items-center">
                <div className="h-[1px] min-h-[1px] w-full bg-[#1B1B1B]" />
                <span className="absolute left-1/2 flex h-12 w-max -translate-x-1/2 items-center justify-center bg-black px-4 text-sm font-medium text-white/50">
                  Loading more...
                </span>
              </div>
            )}
            {!loading && hasNextPage && (
              <div className="relative my-8 flex w-full items-center">
                <div className="h-[1px] min-h-[1px] w-full bg-[#1B1B1B]" />
                <span
                  onClick={() => fetchMoreData()}
                  className="absolute left-1/2 flex h-12 w-max -translate-x-1/2 cursor-pointer items-center justify-center bg-black px-4 text-sm font-medium text-white/50"
                >
                  Load more
                </span>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
